import React from 'react';
import {
    BodyTextSmall,
    BodyTextXSmall,
    HeadingText10,
    HeadingText30,
    Icon,
    IconProps,
    Link,
} from '@modernhealth/vital-components';

import { useMediaQuery } from '@react-hook/media-query';
import { colors, mediaQuery } from '@modernhealth/vital-foundation';
import styled from '@emotion/styled';

interface IconContainerProps {
    smallScreenDetected: boolean;
}

const IconContainer = styled('div')<IconContainerProps>(({ smallScreenDetected }) => {
    return {
        marginRight: 12,
        borderRadius: '50%',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        display: 'flex',

        backgroundColor: colors.gray05,

        width: smallScreenDetected ? 40 : 56,
        height: smallScreenDetected ? 40 : 56,
    };
});

const StaticItemContainer = styled('div')({
    flexDirection: 'column',
    borderRadius: 4,
    border: `2px solid ${colors.gray05}`,
    padding: 12,
    display: 'flex',
});

const StaticItemHeader = styled('div')({
    flexDirection: 'row',
    alignItems: 'center',
    display: 'flex',
});

const StyledText10Component = styled(HeadingText10)({
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    display: 'flex',
    marginBottom: 0,
});
const StyledText30Component = styled(HeadingText30)({
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    display: 'flex',
});

const DescriptionTextContainer = styled('div')({
    marginTop: 8,
});

type StaticItemProps = {
    isMarginTopVisible?: boolean;
    iconName?: IconProps['name'];
    iconUrl?: string;
    title: React.ReactNode;
    description?: string;
    descriptionLinkCta?: string;
    onDescriptionLinkClick?: () => void;
};

const StaticItem: React.FC<StaticItemProps> = ({
    isMarginTopVisible = true,
    iconName,
    iconUrl,
    title,
    description,
    descriptionLinkCta,
    onDescriptionLinkClick,
}) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));

    const TextComponent = smallScreenDetected ? StyledText10Component : StyledText30Component;
    const DescriptionTextComponent = smallScreenDetected ? BodyTextXSmall : BodyTextSmall;

    const renderIcon = () => {
        if (iconName) {
            return <Icon name={iconName} size={smallScreenDetected ? 15 : 21} />;
        }

        if (iconUrl) {
            return (
                <img
                    style={{
                        objectFit: 'cover',
                        objectPosition: 'center center',
                        height: '100%',
                    }}
                    alt=""
                    src={iconUrl}
                />
            );
        }

        return <></>;
    };

    const handleHref = e => {
        e.preventDefault();
        onDescriptionLinkClick?.();
    };

    const showDescriptionLinkCta = !!descriptionLinkCta && !!onDescriptionLinkClick;

    return (
        <StaticItemContainer {...(isMarginTopVisible && { style: { marginTop: 16 } })}>
            <StaticItemHeader>
                <IconContainer smallScreenDetected={smallScreenDetected}>
                    {renderIcon()}
                </IconContainer>
                <TextComponent>{title}</TextComponent>
            </StaticItemHeader>

            {!!description && (
                <DescriptionTextContainer>
                    <DescriptionTextComponent as="p" fontColor="gray85">
                        {description}
                        {showDescriptionLinkCta && (
                            <>
                                {' '}
                                <Link css={{ whiteSpace: 'nowrap' }}>
                                    <span role="button" onClick={handleHref}>
                                        {descriptionLinkCta}
                                    </span>
                                </Link>
                            </>
                        )}
                    </DescriptionTextComponent>
                </DescriptionTextContainer>
            )}
        </StaticItemContainer>
    );
};

type StaticItemListProps = {
    items: StaticItemProps[];
};

export const StaticItemList: React.FC<StaticItemListProps> = ({ items }) => (
    <div>
        {items.map((i, idx) => (
            <StaticItem key={idx} {...i} />
        ))}
    </div>
);
